import { extractPdfPageTexts } from './RRDPdfTextExtractor';
import { PDF_CLASSIFICATION_PAGE_COUNT } from './RRDPdfConfig';

export type ClassifiedDocumentType =
  | 'Appeal Letter'
  | 'Cover Letter'
  | 'Payer Letter'
  | 'Patient Letter'
  | 'Payer Dispute Form'
  | 'Medical Record'
  | 'IZ Statement'
  | 'Ambulance Report'
  | 'Rate Sheet'
  | 'W9 Form'
  | 'EOB'
  | 'PT Statement'
  | 'Charity Denial'
  | 'Charity Denial Over Income'
  | 'Other';

export type DocumentTypeClassification = {
  documentType: ClassifiedDocumentType;
  confidence: number;
  scores: Partial<Record<ClassifiedDocumentType, number>>;
  evidence: string[];
};

type DocumentTypeRule = {
  documentType: ClassifiedDocumentType;
  weight: number;
  evidence: string;
  pattern: RegExp;
};

type DocumentTypeSignalGroup = {
  documentType: ClassifiedDocumentType;
  weight: number;
  evidence: string;
  minimumMatches: number;
  patterns: RegExp[];
};

const HEADER_CHARACTER_LIMIT = 600;

const DOCUMENT_TYPE_RULES: DocumentTypeRule[] = [
  { documentType: 'Appeal Letter', weight: 18, evidence: 'explicit appeal letter title', pattern: /\b(?:appeal\s+letter|letter\s+of\s+appeal|request\s+for\s+appeal|provider\s+appeal\s+form)\b/i },
  { documentType: 'Appeal Letter', weight: 10, evidence: 'sender is appealing a decision', pattern: /\b(?:we|I|provider|hospital|facility)\s+(?:are\s+)?(?:appeal(?:ing)?|contest(?:ing)?|disput(?:e|ing))\b/i },
  { documentType: 'Appeal Letter', weight: 8, evidence: 'decision reconsideration request', pattern: /\b(?:request(?:ing)?|please)\b.{0,80}\b(?:reconsider|reprocess|overturn|reverse|review\s+(?:the|this)\s+(?:denial|decision|determination))\b/i },
  { documentType: 'Appeal Letter', weight: 6, evidence: 'supporting appeal submission', pattern: /\b(?:on\s+behalf\s+of|supporting\s+documentation|medical\s+necessity)\b.{0,100}\b(?:appeal|reconsideration|denial)\b/i },

  { documentType: 'Cover Letter', weight: 18, evidence: 'explicit cover letter title', pattern: /\bcover\s+letter\b/i },
  { documentType: 'Cover Letter', weight: 6, evidence: 'enclosed/attached submission wording', pattern: /\b(?:enclosed|attached|please\s+find|included)\b.{0,80}\b(?:documents?|records?|forms?|information|documentation)\b/i },
  { documentType: 'Cover Letter', weight: 4, evidence: 'introductory routing wording', pattern: /\b(?:to\s+whom\s+it\s+may\s+concern|submitted\s+for\s+processing|for\s+your\s+review)\b/i },

  { documentType: 'Payer Letter', weight: 18, evidence: 'explicit payer decision notice', pattern: /\b(?:notice\s+of\s+(?:action|determination|decision|denial)|determination\s+letter|coverage\s+determination|claim\s+decision|payment\s+determination)\b/i },
  { documentType: 'Payer Letter', weight: 10, evidence: 'payer decision voice', pattern: /\b(?:we|the\s+(?:plan|insurer))\s+(?:have\s+)?(?:denied|approved|determined|reviewed|processed)\b.{0,100}\b(?:claim|request|appeal|authorization|coverage|payment)\b/i },
  { documentType: 'Payer Letter', weight: 8, evidence: 'payer response wording', pattern: /\b(?:your\s+(?:claim|request|appeal)|this\s+letter\s+is\s+to\s+inform\s+you)\b.{0,120}\b(?:denied|approved|upheld|overturned|determination|decision|coverage|payment)\b/i },
  { documentType: 'Payer Letter', weight: 5, evidence: 'insurance organization wording', pattern: /\b(?:health\s+plan|insurance\s+company|member\s+services|provider\s+services|claims\s+department)\b/i },

  { documentType: 'Patient Letter', weight: 18, evidence: 'explicit patient letter title', pattern: /\bpatient\s+letter\b/i },
  { documentType: 'Patient Letter', weight: 10, evidence: 'patient salutation', pattern: /\bdear\s+patient\b/i },
  { documentType: 'Patient Letter', weight: 8, evidence: 'provider message to patient', pattern: /\b(?:your\s+(?:account|bill|visit|care|medical\s+record)|please\s+contact\s+(?:our\s+)?(?:office|billing\s+(?:office|department)))\b/i },
  { documentType: 'Patient Letter', weight: 6, evidence: 'patient payment request', pattern: /\b(?:please\s+(?:pay|remit)|payment\s+is\s+due|contact\s+us\s+to\s+(?:pay|discuss))\b/i },

  { documentType: 'Payer Dispute Form', weight: 22, evidence: 'explicit payer dispute form title', pattern: /\b(?:payer|provider)\s+(?:claim\s+)?dispute\s+(?:resolution\s+request|request\s+form|form)\b/i },
  { documentType: 'Payer Dispute Form', weight: 18, evidence: 'explicit claim dispute form title', pattern: /\b(?:claim\s+dispute\s+form|provider\s+dispute\s+resolution|PDR\s+request)\b/i },
  { documentType: 'Payer Dispute Form', weight: 8, evidence: 'dispute form fields', pattern: /\b(?:reason\s+for\s+dispute|dispute\s+type|disputed\s+amount|expected\s+outcome|original\s+claim\s+(?:number|no\.?|#|ID))\b/i },

  { documentType: 'Medical Record', weight: 8, evidence: 'clinical record section', pattern: /\b(?:discharge\s+summary|history\s+and\s+physical|operative\s+report|progress\s+note|consultation\s+note|clinical\s+note|medical\s+record)\b/i },
  { documentType: 'Medical Record', weight: 6, evidence: 'clinical findings wording', pattern: /\b(?:chief\s+complaint|assessment\s+and\s+plan|physical\s+exam|review\s+of\s+systems|vital\s+signs|diagnosis|medications?|allergies)\b/i },
  { documentType: 'Medical Record', weight: 5, evidence: 'lab/radiology wording', pattern: /\b(?:laboratory\s+results?|radiology\s+report|pathology\s+report|imaging\s+study|test\s+results?)\b/i },

  { documentType: 'IZ Statement', weight: 9, evidence: 'itemized statement label', pattern: /\b(?:itemized\s+statement|itemized\s+bill|charge\s+detail|statement\s+of\s+charges|detailed\s+bill|hospital\s+bill)\b/i },
  { documentType: 'IZ Statement', weight: 7, evidence: 'line-level billing codes', pattern: /\b(?:CPT|HCPCS|revenue\s+code|rev\s+code|procedure\s+code|service\s+code)\b/i },
  { documentType: 'IZ Statement', weight: 6, evidence: 'billing charge columns', pattern: /\b(?:date\s+of\s+service|service\s+date|description)\b.{0,120}\b(?:charges?|amount|qty|quantity|total\s+charges)\b/i },

  { documentType: 'Ambulance Report', weight: 10, evidence: 'ambulance report label', pattern: /\b(?:ambulance\s+(?:report|run|transport)|ems\s+(?:report|run)|patient\s+care\s+report|ePCR|PCR\s+number)\b/i },
  { documentType: 'Ambulance Report', weight: 7, evidence: 'transport details', pattern: /\b(?:paramedic|EMT|unit\s+dispatched|dispatch\s+time|scene\s+time|transported\s+to|pickup\s+location|destination\s+facility|mileage)\b/i },
  { documentType: 'Ambulance Report', weight: 5, evidence: 'ambulance clinical narrative', pattern: /\b(?:loaded\s+(?:patient|pt)|stretcher|lights\s+and\s+sirens|chief\s+complaint)\b/i },

  { documentType: 'Rate Sheet', weight: 10, evidence: 'rate sheet label', pattern: /\b(?:rate\s+sheet|fee\s+schedule|rate\s+schedule|reimbursement\s+schedule|contracted\s+rates?)\b/i },
  { documentType: 'Rate Sheet', weight: 7, evidence: 'rate/payment table columns', pattern: /\b(?:CPT|HCPCS|revenue\s+code|procedure\s+code)\b.{0,120}\b(?:rate|allowed\s+amount|contracted\s+rate|fee|reimbursement)\b/i },
  { documentType: 'Rate Sheet', weight: 5, evidence: 'effective rate wording', pattern: /\b(?:effective\s+date|expiration\s+date|per\s+diem|case\s+rate|DRG\s+rate|negotiated\s+rate)\b/i },

  { documentType: 'W9 Form', weight: 10, evidence: 'W-9 form label', pattern: /\b(?:Form\s+W-?9|W-?9\s+Form|Request\s+for\s+Taxpayer\s+Identification\s+Number\s+and\s+Certification)\b/i },
  { documentType: 'W9 Form', weight: 8, evidence: 'taxpayer identification wording', pattern: /\b(?:taxpayer\s+identification\s+number|TIN|employer\s+identification\s+number|EIN|social\s+security\s+number|SSN)\b/i },
  { documentType: 'W9 Form', weight: 6, evidence: 'W-9 certification wording', pattern: /\b(?:backup\s+withholding|exempt\s+payee\s+code|FATCA\s+reporting|certification\s+instructions|Internal\s+Revenue\s+Service)\b/i },

  { documentType: 'EOB', weight: 10, evidence: 'EOB label', pattern: /\b(?:explanation\s+of\s+benefits|EOB)\b/i },
  { documentType: 'EOB', weight: 8, evidence: 'not a bill wording', pattern: /\bthis\s+is\s+not\s+a\s+bill\b/i },
  { documentType: 'EOB', weight: 7, evidence: 'claim payment columns', pattern: /\b(?:amount\s+billed|billed\s+amount|allowed\s+amount|amount\s+allowed|amount\s+paid|paid\s+amount|plan\s+paid|patient\s+responsibility)\b/i },
  { documentType: 'EOB', weight: 5, evidence: 'adjustment/deductible wording', pattern: /\b(?:deductible|coinsurance|copay|co-pay|adjustment\s+(?:code|reason)|remark\s+code|claim\s+number)\b/i },

];

const HEADER_RULES: DocumentTypeRule[] = [
  { documentType: 'Appeal Letter', weight: 16, evidence: 'appeal title in first-page header', pattern: /\b(?:appeal\s+letter|letter\s+of\s+appeal|request\s+for\s+appeal)\b/i },
  { documentType: 'Cover Letter', weight: 16, evidence: 'cover letter title in first-page header', pattern: /\bcover\s+letter\b/i },
  { documentType: 'Payer Letter', weight: 16, evidence: 'payer notice title in first-page header', pattern: /\b(?:notice\s+of\s+(?:action|determination|decision|denial)|coverage\s+determination|claim\s+decision)\b/i },
  { documentType: 'Patient Letter', weight: 16, evidence: 'patient letter title in first-page header', pattern: /\bpatient\s+letter\b/i },
  { documentType: 'Payer Dispute Form', weight: 20, evidence: 'dispute form title in first-page header', pattern: /\b(?:(?:payer|provider)\s+(?:claim\s+)?dispute|claim\s+dispute\s+form|PDR\s+request)\b/i },
  { documentType: 'Medical Record', weight: 12, evidence: 'clinical report title in first-page header', pattern: /\b(?:medical\s+record|discharge\s+summary|history\s+and\s+physical|operative\s+report)\b/i },
  { documentType: 'IZ Statement', weight: 14, evidence: 'itemized statement title in first-page header', pattern: /\b(?:itemized\s+statement|itemized\s+bill|statement\s+of\s+charges)\b/i },
  { documentType: 'Ambulance Report', weight: 16, evidence: 'ambulance report title in first-page header', pattern: /\b(?:ambulance\s+(?:report|run)|patient\s+care\s+report|EMS\s+report)\b/i },
  { documentType: 'Rate Sheet', weight: 16, evidence: 'rate sheet title in first-page header', pattern: /\b(?:rate\s+sheet|fee\s+schedule|reimbursement\s+schedule)\b/i },
  { documentType: 'W9 Form', weight: 20, evidence: 'W-9 title in first-page header', pattern: /\b(?:Form\s+W-?9|Request\s+for\s+Taxpayer\s+Identification)\b/i },
  { documentType: 'EOB', weight: 18, evidence: 'EOB title in first-page header', pattern: /\b(?:explanation\s+of\s+benefits|EOB)\b/i },
];

const FIELD_SIGNAL_GROUPS: DocumentTypeSignalGroup[] = [
  {
    documentType: 'Payer Dispute Form', weight: 14, evidence: 'dispute form field set', minimumMatches: 3,
    patterns: [/\bprovider\s+name\s*:/i, /\b(?:original\s+)?claim\s+(?:number|no\.?|#|ID)\s*:/i, /\bdispute\s+type\s*:/i, /\breason\s+for\s+dispute\s*:/i, /\bdisputed\s+amount\s*:/i, /\bexpected\s+outcome\s*:/i],
  },
  {
    documentType: 'EOB', weight: 13, evidence: 'benefit explanation field set', minimumMatches: 3,
    patterns: [/\bmember\s+(?:ID|number)\s*:/i, /\bclaim\s+(?:number|no\.?|#)\s*:/i, /\bamount\s+billed\b/i, /\ballowed\s+amount\b/i, /\bplan\s+paid\b/i, /\bpatient\s+responsibility\b/i],
  },
  {
    documentType: 'W9 Form', weight: 16, evidence: 'W-9 field set', minimumMatches: 3,
    patterns: [/\bname\s+as\s+shown\s+on\s+your\s+income\s+tax\s+return/i, /\bbusiness\s+name/i, /\bfederal\s+tax\s+classification/i, /\btaxpayer\s+identification\s+number/i, /\bcertification\b/i],
  },
  {
    documentType: 'Ambulance Report', weight: 12, evidence: 'EMS report field set', minimumMatches: 3,
    patterns: [/\bdispatch\s+time\s*:/i, /\bscene\s+time\s*:/i, /\btransport\s+(?:time|destination)\s*:/i, /\bunit\s+(?:number|ID)\s*:/i, /\bchief\s+complaint\s*:/i],
  },
  {
    documentType: 'Medical Record', weight: 10, evidence: 'clinical section field set', minimumMatches: 3,
    patterns: [/\bchief\s+complaint\b/i, /\bhistory\s+of\s+present\s+illness\b/i, /\bassessment(?:\s+and\s+plan)?\b/i, /\bmedications?\b/i, /\ballergies\b/i, /\bvital\s+signs\b/i],
  },
];

const TABLE_LAYOUT_RULES: DocumentTypeRule[] = [
  { documentType: 'EOB', weight: 14, evidence: 'EOB payment table layout', pattern: /\b(?:amount\s+billed|billed\s+amount)\b.{0,180}\b(?:allowed\s+amount|amount\s+allowed)\b.{0,180}\b(?:plan\s+paid|amount\s+paid|patient\s+responsibility)\b/i },
  { documentType: 'IZ Statement', weight: 13, evidence: 'itemized charge table layout', pattern: /\b(?:date\s+of\s+service|service\s+date)\b.{0,180}\b(?:CPT|HCPCS|procedure|description)\b.{0,180}\b(?:qty|charges?|amount)\b/i },
  { documentType: 'Rate Sheet', weight: 13, evidence: 'rate table layout', pattern: /\b(?:CPT|HCPCS|procedure\s+code|revenue\s+code)\b.{0,180}\b(?:description|service)\b.{0,180}\b(?:rate|fee|allowed\s+amount|reimbursement)\b/i },
  { documentType: 'Payer Dispute Form', weight: 12, evidence: 'dispute form layout', pattern: /\b(?:claim\s+(?:number|no\.?|#)|dispute\s+type)\b.{0,240}\b(?:reason\s+for\s+dispute|disputed\s+amount)\b.{0,240}\b(?:signature|expected\s+outcome|date)\b/i },
];

const NEGATIVE_RULES: DocumentTypeRule[] = [
  { documentType: 'Appeal Letter', weight: 14, evidence: 'payer decision notice contradicts appeal', pattern: /\b(?:notice\s+of\s+(?:determination|decision|denial)|we\s+have\s+(?:denied|approved)|your\s+appeal\s+has\s+been)\b/i },
  { documentType: 'Appeal Letter', weight: 16, evidence: 'structured dispute form contradicts appeal letter', pattern: /\b(?:provider\s+dispute\s+resolution\s+request|claim\s+dispute\s+form)\b/i },
  { documentType: 'Payer Letter', weight: 14, evidence: 'provider appeal voice contradicts payer letter', pattern: /\b(?:we|provider|hospital|facility)\s+(?:are\s+)?appeal(?:ing)?\b/i },
  { documentType: 'Patient Letter', weight: 16, evidence: 'payer decision language contradicts patient letter', pattern: /\b(?:coverage\s+determination|member\s+services|health\s+plan|your\s+(?:claim|appeal)\s+(?:was|has\s+been))\b/i },
  { documentType: 'Patient Letter', weight: 12, evidence: 'benefit statement language contradicts patient letter', pattern: /\b(?:explanation\s+of\s+benefits|this\s+is\s+not\s+a\s+bill|plan\s+paid)\b/i },
  { documentType: 'Cover Letter', weight: 14, evidence: 'specific document title contradicts cover letter', pattern: /\b(?:appeal\s+letter|coverage\s+determination|provider\s+dispute\s+resolution|itemized\s+statement|Form\s+W-?9)\b/i },
  { documentType: 'Payer Dispute Form', weight: 10, evidence: 'unstructured correspondence contradicts dispute form', pattern: /\b(?:dear\s+(?:member|patient|sir|madam)|to\s+whom\s+it\s+may\s+concern)\b/i },
  { documentType: 'Medical Record', weight: 10, evidence: 'attachment reference is not a clinical record', pattern: /\b(?:attached|enclosed)\s+(?:are\s+|is\s+)?(?:the\s+)?medical\s+records?\b/i },
  { documentType: 'EOB', weight: 14, evidence: 'appeal request contradicts EOB', pattern: /\b(?:we\s+are\s+appealing|please\s+(?:reconsider|reprocess|overturn))\b/i },
];

const DOCUMENT_TYPE_TIE_BREAK_ORDER: ClassifiedDocumentType[] = [
  'Payer Dispute Form',
  'Appeal Letter',
  'EOB',
  'IZ Statement',
  'Ambulance Report',
  'W9 Form',
  'Rate Sheet',
  'Medical Record',
  'Payer Letter',
  'Patient Letter',
  'Cover Letter',
  'Other',
];

function countPatternMatches(text: string, pattern: RegExp): number {
  const flags = pattern.flags.includes('g') ? pattern.flags : `${pattern.flags}g`;
  const globalPattern = new RegExp(pattern.source, flags);
  return Array.from(text.matchAll(globalPattern)).length;
}

function classifyDocumentTextWithHeader(fullText: string, headerText: string): DocumentTypeClassification {
  const scores: Partial<Record<ClassifiedDocumentType, number>> = {};
  const evidenceByType: Partial<Record<ClassifiedDocumentType, string[]>> = {};

  const addScore = (documentType: ClassifiedDocumentType, score: number, evidence: string) => {
    scores[documentType] = (scores[documentType] || 0) + score;
    evidenceByType[documentType] = [...(evidenceByType[documentType] || []), evidence];
  };

  for (const rule of DOCUMENT_TYPE_RULES) {
    const matches = countPatternMatches(fullText, rule.pattern);
    if (matches === 0) continue;

    const cappedMatches = Math.min(matches, 3);
    addScore(rule.documentType, rule.weight * cappedMatches, `${rule.evidence}${matches > 1 ? ` (${matches})` : ''}`);
  }

  for (const rule of HEADER_RULES) {
    if (rule.pattern.test(headerText)) addScore(rule.documentType, rule.weight, rule.evidence);
  }

  for (const group of FIELD_SIGNAL_GROUPS) {
    const matches = group.patterns.filter((pattern) => pattern.test(fullText)).length;
    if (matches >= group.minimumMatches) {
      addScore(group.documentType, group.weight + matches, `${group.evidence} (${matches} fields)`);
    }
  }

  for (const rule of TABLE_LAYOUT_RULES) {
    if (rule.pattern.test(fullText)) addScore(rule.documentType, rule.weight, rule.evidence);
  }

  for (const rule of NEGATIVE_RULES) {
    if (rule.pattern.test(fullText) && (scores[rule.documentType] || 0) > 0) {
      addScore(rule.documentType, -rule.weight, `negative: ${rule.evidence}`);
    }
  }

  const ranked = DOCUMENT_TYPE_TIE_BREAK_ORDER
    .map((documentType) => ({ documentType, score: scores[documentType] || 0 }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || DOCUMENT_TYPE_TIE_BREAK_ORDER.indexOf(a.documentType) - DOCUMENT_TYPE_TIE_BREAK_ORDER.indexOf(b.documentType));

  if (ranked.length === 0) {
    return {
      documentType: 'Other',
      confidence: 0.2,
      scores,
      evidence: [],
    };
  }

  const best = ranked[0];
  const secondScore = ranked[1]?.score || 0;
  const totalScore = ranked.reduce((sum, entry) => sum + entry.score, 0);
  const confidence = Math.min(0.98, Math.max(0.55, (best.score / Math.max(totalScore, 1)) * 0.7 + ((best.score - secondScore) / Math.max(best.score, 1)) * 0.3));

  return {
    documentType: best.documentType,
    confidence: Number(confidence.toFixed(2)),
    scores,
    evidence: evidenceByType[best.documentType] || [],
  };
}

export function classifyDocumentText(fullText: string): DocumentTypeClassification {
  return classifyDocumentTextWithHeader(fullText, fullText.slice(0, HEADER_CHARACTER_LIMIT));
}

/**
 * Classifies a document from already-extracted page texts (no PDF parsing).
 * Scores all pages together so the strongest document-level evidence wins.
 */
export function classifyPageTexts(pageTexts: string[]): DocumentTypeClassification {
  const combinedText = pageTexts.join('\n\n');
  const firstPageHeader = (pageTexts[0] || '').slice(0, HEADER_CHARACTER_LIMIT);
  return classifyDocumentTextWithHeader(combinedText, firstPageHeader);
}

export function isClassificationDecisive(classification: DocumentTypeClassification): boolean {
  if (classification.documentType === 'Other' || classification.confidence < 0.85) return false;
  return classification.evidence.some((evidence) => (
    /(?:title|field set|table layout|explicit|payer decision voice|patient salutation)/i.test(evidence)
  ));
}

/**
 * Classifies the whole PDF by collecting text from every page, then scoring all
 * pages together so the strongest document-level evidence wins.
 *
 * Prefer `classifyPageTexts` (via `analyzePdfDocument`) when field extraction
 * is also needed for the same file — this wrapper re-parses the PDF from
 * scratch and is kept for standalone/back-compat use.
 */
export async function classifyPDFDocumentType(
  file: File,
  onDocumentLoaded?: (pageCount: number) => void,
): Promise<DocumentTypeClassification> {
  const pageTexts = await extractPdfPageTexts(file, PDF_CLASSIFICATION_PAGE_COUNT, {
    onDocumentLoaded,
    shouldContinue: (pages) => !isClassificationDecisive(
      classifyPageTexts(pages.map((page) => page.fullText)),
    ),
  });
  return classifyPageTexts(pageTexts.map((p) => p.fullText));
}
